// Promos del teléfono elegido en «Elige el teléfono» y lo que el vendedor marcó de ellas.
import { useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { WIZARD_KEYS } from '@/domains/sales/wizard/api/useWizard';
import type { CatalogItem, WizardSession } from '@/domains/sales/wizard/types';
import {
    useSalePromotionEvaluation,
    useSkuSalePromotions,
    useStoreSalePromotions,
} from '../api/useWizardPromotions';
import type { EvaluatedSalePromotion } from '../api/wizardPromotionsContract';
import { seedFromSession } from '../lib/applySelection';
import {
    carryDraft,
    defaultDraft,
    draftOptionsOf,
    restoreDraft,
    type DraftOption,
    type PhoneDraft,
} from '../lib/phoneDraft';
import { forAnchor } from '../lib/promotionAppliesTo';
import { rememberEvaluatedNames } from '../lib/promotionNames';
import { choicesOn } from '../lib/promotionSelection';
import { inScope } from '../lib/saleBenefits';
import {
    sessionPromotionState,
    usePromotionSelectionStore,
    useSessionPromotionState,
} from '../store/promotionSelectionStore';
import { phoneContextOf } from './phonePromotionsCommit';

export interface PhonePromotionOptions {
    options: DraftOption[];
    /** Ya se leyeron las promos del teléfono (aunque no traiga ninguna). */
    ready: boolean;
    /** Las opciones son de la venta como está hoy, no la lectura anterior que se muestra mientras. */
    settled: boolean;
    isError: boolean;
    retry: () => void;
}

/**
 * Promos del teléfono: antes de apartarlo, las de su SKU; ya apartado, la evaluación de la venta
 * (ahí llega el referido). Solo las que ocupan el lugar del teléfono y le alcanzan.
 */
export function usePhonePromotionOptions(
    sessionId: string,
    item: CatalogItem,
    heldLineId: string | undefined,
    withoutSureKeep: boolean,
): PhonePromotionOptions {
    const held = heldLineId !== undefined;
    const bySku = useSkuSalePromotions(sessionId, item.sku, { enabled: !held });
    const evaluation = useSalePromotionEvaluation(sessionId, { enabled: held });
    const { data: store } = useStoreSalePromotions(sessionId);
    const query = held ? evaluation : bySku;
    const promos: readonly EvaluatedSalePromotion[] | undefined = held
        ? evaluation.data?.promotions
        : bySku.data?.promotions;
    useEffect(() => {
        if (promos) rememberEvaluatedNames(sessionId, { promotions: promos });
    }, [sessionId, promos]);
    const options = useMemo(() => {
        const storeItems = store?.items ?? [];
        const scopes = new Map(storeItems.map((p) => [p.promotionId, p]));
        const phone = forAnchor(
            (promos ?? []).filter((p) => {
                if (!p.anchorBound) return false;
                const scoped = scopes.get(p.promotionId);
                return !scoped || inScope(item, scoped);
            }),
            withoutSureKeep,
        );
        return draftOptionsOf(phone, storeItems, heldLineId);
    }, [promos, store, item, withoutSureKeep, heldLineId]);
    return {
        options,
        ready: promos !== undefined,
        settled: promos !== undefined && !query.isPlaceholderData,
        isError: query.isError,
        retry: () => void query.refetch(),
    };
}

/** Lo apartado en la línea del teléfono: de la memoria si ya la conoce, si no de la sesión. */
function heldOf(
    sessionId: string,
    lineId: string,
    session: WizardSession | undefined,
): { promotionIds: string[]; code: string | null } {
    const state = sessionPromotionState(sessionId);
    if (state.synced && state.selection.anchorLineId === lineId) {
        return {
            promotionIds: choicesOn(state.selection, lineId).map((c) => c.promotionId),
            code: state.selection.code,
        };
    }
    const line = session?.reservedLines?.find((l) => l.lineId === lineId);
    if (!session || !line) return { promotionIds: [], code: null };
    const selection = seedFromSession(phoneContextOf(session, line));
    return {
        promotionIds: choicesOn(selection, lineId).map((c) => c.promotionId),
        code: selection.code,
    };
}

/**
 * Mantiene lo marcado para el teléfono: lo de siempre, lo que siga aplicando o lo apartado.
 * Espera la lectura de la venta actual: la de antes de apartar no trae las promos del teléfono.
 */
export function usePhoneDraft(
    sessionId: string,
    sku: string,
    heldLineId: string | undefined,
    { options, settled }: Pick<PhonePromotionOptions, 'options' | 'settled'>,
): PhoneDraft | undefined {
    const queryClient = useQueryClient();
    const { phoneDraft } = useSessionPromotionState(sessionId);
    const startedFor = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!settled) return;
        const { patch } = usePromotionSelectionStore.getState();
        const state = sessionPromotionState(sessionId);
        const current = state.phoneDraft;
        // Apartarlo en esta visita no cambia lo marcado: es justo lo que se está apartando.
        if (startedFor.current === sku) {
            // Mismo teléfono: solo se refrescan las opciones (versión, existencia, montos).
            if (current?.sku === sku && current.options !== options) {
                patch(sessionId, { phoneDraft: { ...current, options } });
            }
            return;
        }
        startedFor.current = sku;
        const session = queryClient.getQueryData<WizardSession>(WIZARD_KEYS.session(sessionId));
        const next: PhoneDraft = heldLineId
            ? restoreDraft(sku, options, {
                  ...heldOf(sessionId, heldLineId, session),
                  declined: state.saleDeclined ?? false,
              })
            : current?.sku === sku
              ? { ...current, options }
              : current
                ? carryDraft(current, sku, options)
                : defaultDraft(sku, options);
        patch(sessionId, { phoneDraft: next });
    }, [sessionId, sku, heldLineId, options, settled, queryClient]);
    return phoneDraft?.sku === sku ? phoneDraft : undefined;
}

/** Cambia lo marcado del teléfono; sin borrador aún, arranca uno vacío para ese SKU. */
export function updatePhoneDraft(
    sessionId: string,
    sku: string,
    change: (draft: PhoneDraft) => PhoneDraft,
): void {
    const current = sessionPromotionState(sessionId).phoneDraft;
    const base = current?.sku === sku ? current : defaultDraft(sku, [], current?.code ?? null);
    usePromotionSelectionStore.getState().patch(sessionId, { phoneDraft: change(base) });
}
